import { useEffect, useState, useRef, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ProductsList from "../ProductGrid/productsList";
import SearchIcon from "../../assets/images/Search_Icon.png";
import { ProductDetails } from "../../modals/Product";
import { CategoryService, FetchProductsList, SearchAllProducts } from "../../services/services";
import { generalMessages as MSG } from "../../utils/constants";
import { MODULES } from "../../utils/routesNames";
import { debounce } from "lodash";
import { Category } from "../../modals/Category";
import Sidebar from "../Sidebar";
import { findNestedObj, getPathTo } from "../../utils/common";
import HomeIcon from '@mui/icons-material/Home';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SidebarMobile from "../Sidebar/SidebarMobile";
import Loader from "../Loader";
import InfiniteScroll from "react-infinite-scroll-component";

const PAGE_SIZE = 10;

export default function AllProducts() {
    const history = useHistory();
    const location = useLocation();

    // ── Product & pagination state ──
    const [products, setProducts] = useState<ProductDetails[]>([]);
    const [status, setStatus] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    // ── Category state ──
    const [categories, setCategories] = useState<Category[]>([]);
    const [root, setRoot] = useState<Category[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState<number | null>(null);
    const [breadCrumbs, setBreadCrumbs] = useState<string[]>([]);

    // ── Search state ──
    const queryParams = new URLSearchParams(location.search);
    const initialSearch = queryParams.get("search") || "";
    const [searchTerm, setSearchTerm] = useState(initialSearch);

    // Ref to avoid stale closures in loadNextPage
    const activeCategoryRef = useRef<number | undefined>(undefined);

    // When true, infinite scroll is active. False on default landing page (no category, no search).
    const paginationEnabled = useRef(false);

    // ─────────────────────────────────────────────
    // Helpers
    // ─────────────────────────────────────────────

    /** Reset pagination state before a fresh fetch */
    const resetPagination = () => {
        setPage(1);
        setHasMore(true);
        setProducts([]);
        setStatus(null);
    };

    /**
     * Processes a paginated API response (works for both APIs).
     * Expects { data: ProductDetails[], total: number }
     */
    const handlePaginatedResponse = (pageNum: number, data: ProductDetails[], total: number) => {
        if (data.length > 0) {
            if (pageNum === 1) {
                setProducts(data);
            } else {
                setProducts((prev) => [...prev, ...data]);
            }
            // Only allow more pages if pagination is enabled
            setHasMore(paginationEnabled.current && (pageNum * PAGE_SIZE) < total);
        } else {
            if (pageNum === 1) {
                setProducts([]);
                setStatus(MSG.EMPTY_PRODUCTS);
            }
            setHasMore(false);
        }
    };

    // ─────────────────────────────────────────────
    // API: Fetch products by category (paginated)
    // ─────────────────────────────────────────────

    const fetchCategoryProducts = async (pageNum: number, categoryId?: number) => {
        setStatus(null);
        setLoading(true);
        try {
            const response = await FetchProductsList(pageNum, categoryId);
            if (response.data.flag) {
                handlePaginatedResponse(pageNum, response.data.data, response.data.total);
            } else {
                setStatus(MSG.EMPTY_PRODUCTS);
            }
        } catch {
            setStatus(MSG.ERROR_MSG);
        } finally {
            setLoading(false);
        }
    };

    // ─────────────────────────────────────────────
    // API: Search products (paginated)
    // ─────────────────────────────────────────────

    const fetchSearchProducts = async (pageNum: number, term: string) => {
        setStatus(null);
        setLoading(true);
        try {
            const response = await SearchAllProducts({ product_name: term, page: pageNum });
            if (response.data.flag) {
                handlePaginatedResponse(pageNum, response.data.data, response.data.total);
            } else {
                setStatus(MSG.EMPTY_PRODUCTS);
            }
        } catch {
            setStatus(MSG.ERROR_MSG);
        } finally {
            setLoading(false);
        }
    };

    // ─────────────────────────────────────────────
    // Infinite scroll: load next page
    // ─────────────────────────────────────────────

    const loadNextPage = () => {
        const nextPage = page + 1;
        setPage(nextPage);

        if (searchTerm.length >= 3) {
            fetchSearchProducts(nextPage, searchTerm);
        } else {
            fetchCategoryProducts(nextPage, activeCategoryRef.current);
        }
    };

    // ─────────────────────────────────────────────
    // Category helpers (select, jump, navigate)
    // ─────────────────────────────────────────────

    /** Load products for a category (resets pagination) */
    const loadCategory = (categoryId?: number) => {
        activeCategoryRef.current = categoryId;
        // Enable infinite scroll only when a specific category is selected
        paginationEnabled.current = categoryId !== undefined;
        resetPagination();
        fetchCategoryProducts(1, categoryId);
    };

    const handleCategorySelect = (category) => {
        // Scroll to top only for leaf categories / sub-categories (ones that load products)
        if (!category.has_category) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        setSearchTerm("");

        if (category.parent_id) {
            setSelectedSubCategory(category.id);
            setSelectedCategory(category.parent_id);
        } else {
            setSelectedCategory(category.id);
            setSelectedSubCategory(null);
        }

        const path = getPathTo(category.name, root) || [];
        setBreadCrumbs(["Home", ...path]);
        loadCategory(category.id);
        history.push(MODULES.allProducts.url);
    };

    const jumpToCategory = (name: string) => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setSearchTerm("");

        if (name.toLowerCase() === "home" || name.toLowerCase() === "all products") {
            setCategories(root);
            setSelectedCategory(null);
            setSelectedSubCategory(null);
            setBreadCrumbs(["Home", "All Products"]);
            loadCategory(undefined);
            history.push(MODULES.allProducts.url);
            return;
        }

        const item = findNestedObj(root, "name", name);
        const path = getPathTo(item ? item.name : "", root);

        setSelectedCategory(item?.id || null);
        setSelectedSubCategory(null);
        setCategories(root);
        setBreadCrumbs(["Home", ...path]);
        loadCategory(item?.id);
        history.push(MODULES.allProducts.url);
    };

    // ─────────────────────────────────────────────
    // Search: debounced handler
    // ─────────────────────────────────────────────

    const debouncedSearch = useMemo(
        () => debounce((value: string) => {
            resetPagination();
            setSelectedCategory(null);
            activeCategoryRef.current = undefined;
            paginationEnabled.current = true; // Search always has infinite scroll
            fetchSearchProducts(1, value);
        }, 400),
        [] // eslint-disable-line
    );

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (value.length >= 3) {
            setBreadCrumbs(["Home", "All Products", "Search Results"]);
            history.replace({
                pathname: MODULES.allProducts.url,
                search: `?search=${value}`,
            });
            debouncedSearch(value);
        } else {
            setBreadCrumbs(["Home", "All Products"]);
            history.replace(MODULES.allProducts.url);

            // Search dropped below 3 chars — cancel pending search and reset to default view
            debouncedSearch.cancel();
            activeCategoryRef.current = undefined;
            paginationEnabled.current = false;
            loadCategory(undefined);
        }
    };

    // ─────────────────────────────────────────────
    // Initialization
    // ─────────────────────────────────────────────

    // Load categories & initial products
    useEffect(() => {
        const state = location.state as { item?: Category; path?: string[] } | null;

        const initialize = async () => {
            const rootResult = await CategoryService();
            if (!rootResult.data.flag) {
                setLoading(false);
                return;
            }

            setRoot(rootResult.data.data);
            setCategories(rootResult.data.data);

            if (state?.item) {
                // Navigated from another page with a category/sub-category
                const { item, path } = state;

                if (item.has_category === false && path && path.length > 2) {
                    // Sub-category: expand parent, fetch sub-category products
                    const parentCategoryName = path[path.length - 2];
                    const parentCategory = findNestedObj(rootResult.data.data, "name", parentCategoryName);

                    if (parentCategory) {
                        setSelectedCategory(parentCategory.id);
                        setSelectedSubCategory(item.id);
                        loadCategory(item.id);
                    }
                } else {
                    // Regular category
                    setSelectedCategory(item.id);
                    setSelectedSubCategory(null);
                    loadCategory(item.id);
                }

                setBreadCrumbs(["Home", ...(path || [])]);
                history.replace({ ...history.location, state: null });
            } else if (initialSearch.length >= 3) {
                // Page loaded with ?search=... in URL
                setBreadCrumbs(["Home", "All Products", "Search Results"]);
                paginationEnabled.current = true;
                resetPagination();
                fetchSearchProducts(1, initialSearch);
            } else {
                // Default: show all products (no infinite scroll)
                setBreadCrumbs(["Home", "All Products"]);
                loadCategory(undefined);
            }
        };

        initialize();
    }, []); // eslint-disable-line

    // ─────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────

    return (
        <div className="product-wrapper container-xl">
            <div className="">
                {/* Search Input */}
                <div className="form-group" style={{ position: "relative" }}>
                    <input
                        value={searchTerm}
                        placeholder="Search All Products"
                        className="form-control"
                        onChange={handleSearch}
                    />
                    <img
                        src={SearchIcon}
                        alt="Search"
                        style={{
                            position: "absolute",
                            right: 18,
                            bottom: 10,
                            height: 30,
                        }}
                    />
                </div>

                {/* Breadcrumbs */}
                <div className="primary-breadcrumb">
                    <div className="breadcrumb">
                        {breadCrumbs.map((item, index) => {
                            const isLast = index === breadCrumbs.length - 1;
                            const displayName = item === "Home"
                                ? <HomeIcon className="text-muted" />
                                : item;

                            return (
                                <span key={index}>
                                    <span
                                        className={`breadcrumb-item ${isLast ? 'active' : ''}`}
                                        onClick={() => {
                                            if (!isLast) jumpToCategory(item.toLowerCase() === "home" ? "home" : item);
                                        }}
                                        style={{ cursor: !isLast ? 'pointer' : 'default' }}
                                    >
                                        {displayName}
                                    </span>
                                    {!isLast && <ChevronRightIcon className="breadcrumb-separator" />}
                                </span>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="row">
                {/* Sidebar */}
                <div className="sideNavWrap col-md-4 col-xl-3">
                    <Sidebar
                        categories={categories}
                        onCategorySelect={handleCategorySelect}
                        selectedCategoryId={selectedCategory}
                        selectedSubCategoryId={selectedSubCategory}
                        isLoading={loading && page === 1}
                    />
                    <SidebarMobile
                        categories={categories}
                        onCategorySelect={handleCategorySelect}
                        selectedCategoryId={selectedCategory}
                        selectedSubCategoryId={selectedSubCategory}
                    />
                </div>

                {/* Product Content */}
                <div className="col-md-8 col-xl-9">
                    <div className="container-xl">
                        {loading && page === 1 ? <Loader /> : (
                            <InfiniteScroll
                                dataLength={products.length}
                                next={loadNextPage}
                                hasMore={hasMore}
                                loader={loading ? <Loader /> : null}
                                style={{ overflow: 'visible' }}
                            >
                                <ProductsList status={status} products={products} />
                            </InfiniteScroll>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
